import { useCallback, useState, useRef } from 'react';
import Draggable, { DraggableEvent, DraggableData } from 'react-draggable';
import { useDispatch } from 'react-redux';

import {
  CalloutType,
  RunesCalloutType,
  RUNES_CALLOUT_TYPES,
  RunesCallout,
} from '@frontend/api';
import {
  Box,
  Select,
  FormControl,
  FormLabel,
  Heading,
  CloseIcon,
  IconButton,
  DragHandleIcon,
  HStack,
  Container,
  ContainerHeader,
} from '@frontend/legacy-components';

import { SUGGESTION_ANNOTATION_COMMAND_TYPES } from '../annotationCommands';
import { suggestionAnnotations } from '../data/actions';
import { BendLineForm } from './forms/BendLineForm';
import { ChamferForm } from './forms/ChamferForm';
import { CompositeControlFrameForm } from './forms/CompositeControlFrameForm';
import { ControlFrameForm } from './forms/ControlFrameForm';
import { CounterboreForm } from './forms/CounterboreForm';
import { CountersinkForm } from './forms/CountersinkForm';
import { DatumForm } from './forms/DatumForm';
import { DimensionForm } from './forms/DimensionForm';
import { HoleForm } from './forms/HoleForm';
import { SurfaceFinishForm } from './forms/SurfaceFinishForm';
import { ThreadForm } from './forms/ThreadForm';
import { WeldForm } from './forms/WeldForm';
import { SubmitCallback } from './types';
import type { CalloutAuthorInfo } from './useCalloutAuthor';

type CalloutTypeOption = {
  label: string;
  value: RunesCalloutType | string;
  isDisabled?: boolean;
};

const SUPPORTED_OPTIONS: CalloutTypeOption[] = [
  { label: 'Dimension', value: RUNES_CALLOUT_TYPES.DIM },
  { label: 'Control Frame', value: RUNES_CALLOUT_TYPES.CONTROL_FRAME },
  {
    label: 'Composite Control Frame',
    value: RUNES_CALLOUT_TYPES.COMPOSITE_CONTROL_FRAME,
  },
  { label: 'Hole', value: RUNES_CALLOUT_TYPES.HOLE },
  { label: 'Counterbore', value: RUNES_CALLOUT_TYPES.COUNTERBORE },
  { label: 'Countersink', value: RUNES_CALLOUT_TYPES.COUNTERSINK },
  { label: 'Datum', value: RUNES_CALLOUT_TYPES.DATUM },
  { label: 'Weld', value: RUNES_CALLOUT_TYPES.WELD },
  { label: 'Thread', value: RUNES_CALLOUT_TYPES.THREAD },
  { label: 'Surface Finish', value: RUNES_CALLOUT_TYPES.SURFACE_FINISH },
  { label: 'Bend Line', value: RUNES_CALLOUT_TYPES.BEND_LINE },
  { label: 'Chamfer', value: RUNES_CALLOUT_TYPES.CHAMFER },
];

const SELECT_OPTIONS = [...SUPPORTED_OPTIONS].sort((a, b) =>
  a.label.localeCompare(b.label),
);

type CalloutAuthorProps = {
  activeFileUuid: string | null;
  calloutAuthorInfo: CalloutAuthorInfo | null;
  handleCloseCalloutAuthor: () => void;
};

export const CalloutAuthor = ({
  activeFileUuid,
  calloutAuthorInfo,
  handleCloseCalloutAuthor,
}: CalloutAuthorProps) => {
  const [selectedType, setSelectedType] = useState<CalloutTypeOption | null>(
    SUPPORTED_OPTIONS[0],
  );
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const nodeRef = useRef(null);
  const dispatch = useDispatch();

  const handleSubmit: SubmitCallback = useCallback(
    (callout: CalloutType, rawText?: string) => {
      if (!calloutAuthorInfo?.loc || !activeFileUuid) return;

      const runesCalloutPayload: RunesCallout = {
        callout,
        loc: calloutAuthorInfo.loc,
        rawText: rawText ?? '',
        desc: null,
      };

      dispatch(
        suggestionAnnotations.queueCommands({
          commands: [
            {
              type: SUGGESTION_ANNOTATION_COMMAND_TYPES.CREATE_PRINT_EXTRACTION,
              payload: {
                callout: runesCalloutPayload,
                fileUuid: activeFileUuid,
                pageNumber: calloutAuthorInfo.pageNumber,
              },
            },
          ],
        }),
      );

      handleCloseCalloutAuthor();
    },
    [
      calloutAuthorInfo?.loc,
      calloutAuthorInfo?.pageNumber,
      activeFileUuid,
      dispatch,
      handleCloseCalloutAuthor,
    ],
  );
  const handleCancel = useCallback(() => {
    handleCloseCalloutAuthor();
  }, [handleCloseCalloutAuthor]);

  const handleDragStop = (e: DraggableEvent, data: DraggableData) => {
    setPosition({ x: data.x, y: data.y });
  };

  const renderSelectedType = useCallback(() => {
    return (
      <FormControl p={1}>
        <FormLabel id="callout-type-label">Callout Type</FormLabel>
        <Select
          options={SELECT_OPTIONS}
          value={selectedType}
          onChange={(value: CalloutTypeOption) => setSelectedType(value)}
          isClearable={false}
        />
      </FormControl>
    );
  }, [selectedType, setSelectedType]);

  const renderForm = useCallback(() => {
    switch (selectedType?.value) {
      case RUNES_CALLOUT_TYPES.DIM:
        return (
          <DimensionForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            renderSelectedType={renderSelectedType}
          />
        );
      case RUNES_CALLOUT_TYPES.CONTROL_FRAME:
        return (
          <ControlFrameForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            renderSelectedType={renderSelectedType}
          />
        );
      case RUNES_CALLOUT_TYPES.COMPOSITE_CONTROL_FRAME:
        return (
          <CompositeControlFrameForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            renderSelectedType={renderSelectedType}
          />
        );
      case RUNES_CALLOUT_TYPES.HOLE:
        return (
          <HoleForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            renderSelectedType={renderSelectedType}
          />
        );
      case RUNES_CALLOUT_TYPES.COUNTERBORE:
        return (
          <CounterboreForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            renderSelectedType={renderSelectedType}
          />
        );
      case RUNES_CALLOUT_TYPES.COUNTERSINK:
        return (
          <CountersinkForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            renderSelectedType={renderSelectedType}
          />
        );
      case RUNES_CALLOUT_TYPES.DATUM:
        return (
          <DatumForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            renderSelectedType={renderSelectedType}
          />
        );
      case RUNES_CALLOUT_TYPES.WELD:
        return (
          <WeldForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            renderSelectedType={renderSelectedType}
          />
        );
      case RUNES_CALLOUT_TYPES.THREAD:
        return (
          <ThreadForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            renderSelectedType={renderSelectedType}
          />
        );
      case RUNES_CALLOUT_TYPES.SURFACE_FINISH:
        return (
          <SurfaceFinishForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            renderSelectedType={renderSelectedType}
          />
        );
      case RUNES_CALLOUT_TYPES.BEND_LINE:
        return (
          <BendLineForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            renderSelectedType={renderSelectedType}
          />
        );
      case RUNES_CALLOUT_TYPES.CHAMFER:
        return (
          <ChamferForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            renderSelectedType={renderSelectedType}
          />
        );
      default:
        return null;
    }
  }, [selectedType, handleSubmit, handleCancel, renderSelectedType]);

  if (!calloutAuthorInfo) {
    return null;
  }

  return (
    <Draggable
      nodeRef={nodeRef}
      position={position}
      onStop={handleDragStop}
      handle=".drag-handle"
      bounds="parent"
    >
      <Container
        ref={nodeRef}
        display="flex"
        flexDirection="column"
        position="absolute"
        top={16}
        left={4}
        width={45}
        pointerEvents="auto"
        shadow="popover"
        size="sm"
        variant="default"
        alignItems="stretch"
      >
        <ContainerHeader
          transition="all 0.2s ease-in-out"
          _hover={{
            bg: 'interaction.surfaceHover',
          }}
        >
          <HStack
            spacing={2}
            className="drag-handle"
            cursor="move"
            width="100%"
            justify="space-between"
          >
            <HStack spacing={1}>
              <DragHandleIcon height={2.5} color="feature.secondary" />
              <Heading m={0} size="lg">
                Add missing extraction
              </Heading>
            </HStack>
            <IconButton onClick={handleCloseCalloutAuthor} size="sm">
              <CloseIcon />
            </IconButton>
          </HStack>
        </ContainerHeader>
        <Box>{renderForm()}</Box>
      </Container>
    </Draggable>
  );
};
